/**
 * @file AppRouter.js
 * @description Router principal de la aplicación usando react-router-dom v6
 * @status ACTIVE - Este es el router oficial de la aplicación
 * @routes
 *   - / : Página de inicio
 *   - /calculadora : Calculadora simple de energía
 *   - /ecotrim : Simulador avanzado Ecotrim
 *   - /mi-area/* : Área de cliente (protegida)
 *   - /register/* : Flujos de registro
 *   - /subscription/success : Éxito de suscripción
 * @see App.js para compatibilidad legacy (a eliminar)
 */

import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './components/InicioHeader.css';
import './components/NewInicioHeader.css';
import './components/Header.css';

// Importar componentes existentes
import AccessMenu from './components/Users/AccessMenu';
import ScrollToTop from './components/ScrollToTop';
import DiscardUnpaidSignup from './components/DiscardUnpaidSignup';
import EnsureTrailingSlash from './components/EnsureTrailingSlash';
import { stripTrailingSlash } from './utils/pathNormalization';
import Home from './pages/Home';

import { CalculatorProvider } from './context/CalculatorContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { useBackToHome } from './hooks/useClientAreaNavigation';
import { useEnsureBrowserScroll } from './hooks/useEnsureBrowserScroll';
import LandingLayout from './components/LandingComponents/LandingLayout';
import EcotrimProtectedRoute from './components/ProtectedRoutes/EcotrimProtectedRoute';
import AdminRoute from './components/ProtectedRoutes/AdminRoute';
import PlatformAdminRoute from './components/ProtectedRoutes/PlatformAdminRoute';
import WhatsAppFloat from './components/WhatsAppFloat/WhatsAppFloat';
import CookieConsentBanner from './components/CookieConsent/CookieConsentBanner';
import CookiePreferencesTrigger from './components/CookieConsent/CookiePreferencesTrigger';
import PostHogWithConsent from './components/PostHogWithConsent';
import { CookieConsentProvider } from './context/CookieConsentContext';
import RouteSuspenseFallback from './components/Skeletons/RouteSuspenseFallback';
import { ClientAreaSkeleton } from './components/Skeletons';

const CalculatorStandalone = lazy(() => import('./components/CalculatorComponents/CalculatorStandalone'));
const Privacity = lazy(() => import('./components/Information/Privacity'));
const Accesibility = lazy(() => import('./components/Information/Accesibility'));
const LegalAdvise = lazy(() => import('./components/Information/LegalAdvise'));
const Cookies = lazy(() => import('./components/Information/Cookies'));
const UserLogin = lazy(() => import('./components/Users/UserLogin'));
const RegisterFlow = lazy(() => import('./components/Users/RegisterFlow'));
const UserArea = lazy(() => import('./components/Users/UserArea'));
const SimulationHistory = lazy(() => import('./components/CalculatorComponents/SimulationHistory'));
const DebugReports = lazy(() => import('./components/Debug/DebugReports'));
const NavigationTest = lazy(() => import('./components/NavigationTest'));
const PaymentSuccess = lazy(() => import('./components/Payment/PaymentSuccess'));
const SubscriptionSuccess = lazy(() => import('./components/SubscriptionSuccess/SubscriptionSuccess'));
const Ecotrim = lazy(() => import('./components/Ecotrim/Ecotrim'));
const GeotrimCatalog = lazy(() =>
  import('./components/GeotrimCatalog').then((module) => ({ default: module.GeotrimCatalog }))
);
const EcotrimArea = lazy(() =>
  import('./components/EcotrimArea').then((module) => ({ default: module.EcotrimArea }))
);
const Pricing = lazy(() => import('./pages/Pricing'));
const Contact = lazy(() => import('./pages/Contact'));
const Login = lazy(() => import('./pages/Login'));
const Calc = lazy(() => import('./pages/Calc'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const GeotrimProduct = lazy(() => import('./pages/GeotrimProduct'));
const EcotrimProduct = lazy(() => import('./pages/EcotrimProduct'));
const HomeV2 = lazy(() => import('./pages/HomeV2'));
const ParaQuienIndex = lazy(() => import('./pages/ParaQuienIndex'));
// const ParaQuienIngenieriaEnergetica = lazy(() => import('./pages/ParaQuienIngenieriaEnergetica'));
const ParaQuienFabricantesBaterias = lazy(() => import('./pages/ParaQuienFabricantesBaterias'));
const ParaQuienAlimentacionFrio = lazy(() => import('./pages/ParaQuienAlimentacionFrio'));
const ParaQuienMovilidadElectrica = lazy(() => import('./pages/ParaQuienMovilidadElectrica'));
const ParaQuienCeramicaPorcelanico = lazy(() => import('./pages/ParaQuienCeramicaPorcelanico'));
const ParaQuienDataCenters = lazy(() => import('./pages/ParaQuienDataCenters'));
// Landing ESE oculta hasta que se active: /para-quien/empresas-servicios-energeticos
// const ParaQuienEmpresasServiciosEnergeticos = lazy(() =>
//   import('./pages/ParaQuienEmpresasServiciosEnergeticos')
// );
const ParaQuienInstaladoresIngenieriasEnergeticas = lazy(() =>
  import('./pages/ParaQuienInstaladoresIngenieriasEnergeticas')
);
// const ParaQuienInstaladores = lazy(() => import('./pages/ParaQuienInstaladores'));
const BlogIndex = lazy(() => import('./pages/BlogIndex'));
const BlogPeakShavingBateriasIndustrial = lazy(() => import('./pages/BlogPeakShavingBateriasIndustrial'));
const BlogAutoconsumoColectivoVsIndividual = lazy(() => import('./pages/BlogAutoconsumoColectivoVsIndividual'));
const BlogEficienciaEnergeticaIndustrial = lazy(() => import('./pages/BlogEficienciaEnergeticaIndustrial'));
const BlogExcedentesAutoconsumoEmpresa = lazy(() => import('./pages/BlogExcedentesAutoconsumoEmpresa'));
const BlogCalculadorasSolaresConsumoUbicacion = lazy(
  () => import('./pages/BlogCalculadorasSolaresConsumoUbicacion')
);
const BlogSimuladorComunidadEnergetica = lazy(() => import('./pages/BlogSimuladorComunidadEnergetica'));
const BlogSoftwareInstaladoresBaterias = lazy(() => import('./pages/BlogSoftwareInstaladoresBaterias'));
const BlogRealDecreto2442019 = lazy(() => import('./pages/BlogRealDecreto2442019'));
const BlogMapaPlacasSolaresEspana = lazy(() => import('./pages/BlogMapaPlacasSolaresEspana'));
const BlogPlacasSolaresNaveIndustrial = lazy(() => import('./pages/BlogPlacasSolaresNaveIndustrial'));
const NotFound = lazy(() => import('./pages/NotFound'));
const ValidationDashboard = lazy(() => import('./components/ValidationDashboard/ValidationDashboard'));
// Panel de administración (IT-700): solo platform admins
const PlatformAdminArea = lazy(() => import('./components/PlatformAdmin/PlatformAdminArea'));
// Consentimiento OAuth del MCP remoto (IT-976)
const McpAuthorize = lazy(() => import('./pages/McpAuthorize'));

// Componente wrapper para manejar navegación programática
const NavigationWrapper = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // Función para navegar programáticamente
  const handleNavigation = (path) => {
    navigate(path);
    setMenuOpen(false); // Cerrar menú al navegar
  };

  // Detectar si estamos en el área de usuario o Ecotrim
  const isUserArea = location.pathname.startsWith('/mi-area');
  const isEcotrimLanding = stripTrailingSlash(location.pathname) === '/ecotrim';
  const isEcotrim =
    !isEcotrimLanding &&
    (location.pathname.startsWith('/ecotrim') || location.pathname.startsWith('/nueva-simulacion'));
  const isEcotrimArea = location.pathname.startsWith('/ecotrim-area');
  const isDatadis = location.pathname.startsWith('/datadis');
  const isPricing = location.pathname.startsWith('/pricing');
  const isContact = location.pathname.startsWith('/contact');
  const isLogin = location.pathname.startsWith('/login');
  const isCalc = location.pathname.startsWith('/calc');
  const isLegal = location.pathname.startsWith('/legal');
  const isPrivacity = location.pathname.startsWith('/privacidad');
  const isCookies = location.pathname.startsWith('/cookies');
  const isHome = stripTrailingSlash(location.pathname) === '/';

  // En áreas de trabajo, NO mostrar header
  const isWorkArea = isUserArea || isEcotrim || isEcotrimArea || isDatadis

  if(!isWorkArea) {
    return (
      <>
      <LandingLayout>
      <main
        style={{
          flex: 1,
          paddingTop:'0',
          paddingLeft:'0',
          paddingRight:'0',
          position: 'relative',
          zIndex: 1
        }}
      >
        {children}
      </main>
      </LandingLayout>
      </>
    );
  }

  if (isWorkArea) {
    return (
      <>
      <AccessMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        onSelect={handleNavigation}
        onNavigate={handleNavigation}
      />
      <main
        style={{
          flex: 1,
          paddingTop: isWorkArea ? '0' : '80px',
          paddingLeft: isWorkArea ? '0' : '20px',
          paddingRight: isWorkArea ? '0' : '20px',
          position: 'relative',
          zIndex: 1
        }}
      >
{children}
      </main>
        
    </>
    );
  }
};

// Redirige al login en cuanto la sesión caduca (401 en cualquier API)
const SessionExpiredRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleSessionExpired = () => {
      navigate('/login', {
        replace: true,
        state: {
          from: location.pathname,
          message: 'Tu sesión ha expirado. Por favor, inicia sesión de nuevo.',
        },
      });
    };

    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, [navigate, location.pathname]);

  return null;
};

// Componente para rutas protegidas
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/login', {
        replace: true,
        state: {
          from: location.pathname,
          message: 'Necesitas iniciar sesión para acceder a esta página.',
        },
      });
    }
  }, [isAuthenticated, isLoading, navigate, location.pathname]);

  // Si hay token en storage, mostrar skeleton del área; si no, nada (va a login).
  if (isLoading && !isAuthenticated) {
    const hasStoredSession = !!localStorage.getItem('authToken');
    return hasStoredSession ? <ClientAreaSkeleton variant="tools" /> : null;
  }

  return isAuthenticated ? (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {children}
    </>
  ) : null;
};

// Componente principal de rutas
const AppRoutes = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [ecotrimKey, setEcotrimKey] = useState(0);
  // IT-903: navegar entre simulaciones dentro de /ecotrim/:simulationId reutiliza
  // la instancia de Ecotrim (su simulationId vive en estado). Con
  // `state.remountEcotrim` se fuerza el remontaje en el mismo render.
  const [remountedLocationKey, setRemountedLocationKey] = useState(null);
  if (location.state?.remountEcotrim && location.key !== remountedLocationKey) {
    setRemountedLocationKey(location.key);
    setEcotrimKey(prev => prev + 1);
  }

  // Hook para garantizar que el scroll del navegador esté siempre disponible
  useEnsureBrowserScroll();

  // Hook para manejar navegación hacia atrás desde área de cliente
  useBackToHome();

  // Incrementar key de Ecotrim al abrir una simulación nueva
  useEffect(() => {
    if (stripTrailingSlash(location.pathname) === '/ecotrim/nueva') {
      setEcotrimKey(prev => prev + 1);
    }
  }, [location.pathname]);

  // GeoTrim se monta igual con y sin segmento de sección (IT-805): el elemento
  // se define una vez para que las dos rutas compartan exactamente las props.
  const geotrimCatalogElement = (
    <ProtectedRoute>
      <GeotrimCatalog
        onNavigate={navigate}
        onBack={() => navigate('/mi-area')}
        onSelectSite={(siteData) => {
          // Guardar datos del sitio en sessionStorage para usar en Ecotrim
          sessionStorage.setItem('catalogSiteData', JSON.stringify(siteData));
          navigate('/ecotrim/nueva');
        }}
      />
    </ProtectedRoute>
  );

  return (
    <NavigationWrapper>
      <SessionExpiredRedirect />
      <Suspense fallback={<RouteSuspenseFallback />}>
        <Routes>
        {/* Calculadora */}
        <Route path="/calculadora" element={<CalculatorStandalone onNavigate={navigate} />} />

        {/* Legacy SEO URL → pricing */}
        <Route path="/precios" element={<Navigate to="/pricing/" replace />} />

        {/* Ruta pricing */}
        <Route
          path="/pricing"
          element={
            <Pricing />
          }
        />

        {/* Ruta contact */}
        <Route
          path="/contact"
          element={
            <Contact />
          }
        />

        {/* Ruta about us */}
        <Route
          path="/nosotros"
          element={
            <AboutUs />
          }
        />

        {/* Ruta login */}
        <Route
          path="/login"
          element={
            <Login />
          }
        />

        {/* Ruta calc */}
        <Route
          path="/calc"
          element={
            <Calc />
          }
        />

        {/* Landing Geotrim */}
        <Route
          path="/geotrim"
          element={
            <GeotrimProduct />
          }
        />

        {/* Landing Ecotrim */}
        <Route
          path="/ecotrim"
          element={
            <EcotrimProduct />
          }
        />

        {/* Para quién — índice */}
        <Route
          path="/para-quien"
          element={
            <ParaQuienIndex />
          }
        />

        {/* Para quién — Ingeniería energética (oculta temporalmente)
        <Route
          path="/para-quien/ingenieria-energetica"
          element={
            <ParaQuienIngenieriaEnergetica />
          }
        />
        */}

        {/* Para quién — Instaladores e ingenierías energéticas */}
        <Route
          path="/para-quien/instaladores-ingenierias-energeticas"
          element={
            <ParaQuienInstaladoresIngenieriasEnergeticas />
          }
        />

        {/* Para quién — Fabricantes de baterías */}
        <Route
          path="/para-quien/fabricantes-baterias"
          element={
            <ParaQuienFabricantesBaterias />
          }
        />

        {/* Para quién — Alimentación y frío industrial */}
        <Route
          path="/para-quien/alimentacion-frio"
          element={
            <ParaQuienAlimentacionFrio />
          }
        />

        {/* Para quién — Movilidad eléctrica */}
        <Route
          path="/para-quien/movilidad-electrica"
          element={
            <ParaQuienMovilidadElectrica />
          }
        />

        {/* Para quién — Cerámica y porcelánico */}
        <Route
          path="/para-quien/ceramica-porcelanico"
          element={
            <ParaQuienCeramicaPorcelanico />
          }
        />

        {/* Para quién — Data centers */}
        <Route
          path="/para-quien/data-centers"
          element={
            <ParaQuienDataCenters />
          }
        />

        {/* Para quién — Empresas de servicios energéticos (oculta hasta activación) */}
        <Route
          path="/para-quien/empresas-servicios-energeticos"
          element={<Navigate to="/para-quien/" replace />}
        />

        {/* Blog */}
        <Route
          path="/blog"
          element={
            <BlogIndex />
          }
        />
        <Route
          path="/blog/peak-shaving-baterias-industrial"
          element={
            <BlogPeakShavingBateriasIndustrial />
          }
        />
        <Route
          path="/blog/autoconsumo-colectivo-vs-individual-empresas"
          element={
            <BlogAutoconsumoColectivoVsIndividual />
          }
        />
        <Route
          path="/blog/eficiencia-energetica-industrial"
          element={
            <BlogEficienciaEnergeticaIndustrial />
          }
        />
        <Route
          path="/blog/excedentes-autoconsumo-empresa"
          element={
            <BlogExcedentesAutoconsumoEmpresa />
          }
        />
        <Route
          path="/blog/calculadoras-solares-consumo-ubicacion"
          element={
            <BlogCalculadorasSolaresConsumoUbicacion />
          }
        />
        <Route
          path="/blog/simulador-comunidad-energetica"
          element={
            <BlogSimuladorComunidadEnergetica />
          }
        />
        <Route
          path="/blog/software-instaladores-baterias"
          element={
            <BlogSoftwareInstaladoresBaterias />
          }
        />
        <Route
          path="/blog/real-decreto-244-2019-autoconsumo"
          element={
            <BlogRealDecreto2442019 />
          }
        />
        <Route
          path="/blog/mapa-placas-solares-espana"
          element={
            <BlogMapaPlacasSolaresEspana />
          }
        />
        <Route
          path="/blog/placas-solares-nave-industrial"
          element={
            <BlogPlacasSolaresNaveIndustrial />
          }
        />

        {/* Para quién — Instaladores (oculta temporalmente)
        <Route
          path="/para-quien/instaladores"
          element={
            <ParaQuienInstaladores />
          }
        />
        */}

        <Route
          path="/"
          element={
            <HomeV2 />
          }
        />

        {/* Home anterior (backup) */}
        <Route
          path="/home-v1"
          element={
            <Home />
          }
        />

        <Route path="/home-v2" element={<Navigate to="/" replace />} />

        <Route
          path="/nueva-simulacion"
          element={<CalculatorStandalone onNavigate={navigate} />}
        />

        {/* Ecotrim - Nuevo simulador - PROTEGIDO CON VERIFICACIÓN DE SUSCRIPCIÓN */}
        <Route
          path="/ecotrim/nueva"
          element={
            <ProtectedRoute>
              <EcotrimProtectedRoute>
                <Ecotrim
                  key={ecotrimKey}
                  onNavigate={navigate}
                  onBack={() => navigate('/ecotrim-area')}
                />
              </EcotrimProtectedRoute>
            </ProtectedRoute>
          }
        />

        {/* Ruta base del simulador con ID de simulación */}
        <Route
          path="/ecotrim/:simulationId"
          element={
            <ProtectedRoute>
              <EcotrimProtectedRoute>
                <Ecotrim
                  key={ecotrimKey}
                  onNavigate={navigate}
                  onBack={() => navigate('/ecotrim-area')}
                />
              </EcotrimProtectedRoute>
            </ProtectedRoute>
          }
        />

        {/* Rutas para las diferentes secciones de Ecotrim */}
        <Route
          path="/ecotrim/:simulationId/fotovoltaica"
          element={
            <ProtectedRoute>
              <EcotrimProtectedRoute>
                <Ecotrim
                  key={ecotrimKey}
                  onNavigate={navigate}
                  onBack={() => navigate('/ecotrim-area')}
                />
              </EcotrimProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/ecotrim/:simulationId/demanda"
          element={
            <ProtectedRoute>
              <EcotrimProtectedRoute>
                <Ecotrim
                  key={ecotrimKey}
                  onNavigate={navigate}
                  onBack={() => navigate('/ecotrim-area')}
                />
              </EcotrimProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/ecotrim/:simulationId/modelo-operacion"
          element={
            <ProtectedRoute>
              <EcotrimProtectedRoute>
                <Ecotrim
                  key={ecotrimKey}
                  onNavigate={navigate}
                  onBack={() => navigate('/ecotrim-area')}
                />
              </EcotrimProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/ecotrim/:simulationId/precios"
          element={
            <ProtectedRoute>
              <EcotrimProtectedRoute>
                <Ecotrim
                  key={ecotrimKey}
                  onNavigate={navigate}
                  onBack={() => navigate('/ecotrim-area')}
                />
              </EcotrimProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/ecotrim/:simulationId/almacenamiento"
          element={
            <ProtectedRoute>
              <EcotrimProtectedRoute>
                <Ecotrim
                  key={ecotrimKey}
                  onNavigate={navigate}
                  onBack={() => navigate('/ecotrim-area')}
                />
              </EcotrimProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/ecotrim/:simulationId/modelo-economico"
          element={
            <ProtectedRoute>
              <EcotrimProtectedRoute>
                <Ecotrim
                  key={ecotrimKey}
                  onNavigate={navigate}
                  onBack={() => navigate('/ecotrim-area')}
                />
              </EcotrimProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/ecotrim/:simulationId/informe"
          element={
            <ProtectedRoute>
              <EcotrimProtectedRoute>
                <Ecotrim
                  key={ecotrimKey}
                  onNavigate={navigate}
                  onBack={() => navigate('/ecotrim-area')}
                />
              </EcotrimProtectedRoute>
            </ProtectedRoute>
          }
        />

        {/* Autoconsumo Colectivo — Secciones adicionales (IT-113) — SOLO ADMIN */}
        <Route
          path="/ecotrim/:simulationId/instalacion-generadora"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <EcotrimProtectedRoute>
                  <Ecotrim
                    key={ecotrimKey}
                    onNavigate={navigate}
                    onBack={() => navigate('/ecotrim-area')}
                  />
                </EcotrimProtectedRoute>
              </AdminRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/ecotrim/:simulationId/miembros"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <EcotrimProtectedRoute>
                  <Ecotrim
                    key={ecotrimKey}
                    onNavigate={navigate}
                    onBack={() => navigate('/ecotrim-area')}
                  />
                </EcotrimProtectedRoute>
              </AdminRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/ecotrim/:simulationId/reparto"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <EcotrimProtectedRoute>
                  <Ecotrim
                    key={ecotrimKey}
                    onNavigate={navigate}
                    onBack={() => navigate('/ecotrim-area')}
                  />
                </EcotrimProtectedRoute>
              </AdminRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/ecotrim/:simulationId/modelo-economico-colectivo"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <EcotrimProtectedRoute>
                  <Ecotrim
                    key={ecotrimKey}
                    onNavigate={navigate}
                    onBack={() => navigate('/ecotrim-area')}
                  />
                </EcotrimProtectedRoute>
              </AdminRoute>
            </ProtectedRoute>
          }
        />

        {/* GeoTrim Catalog - Catálogo de ubicaciones (gratuito para usuarios autenticados) */}
        <Route
          path="/ecotrim/catalog"
          element={geotrimCatalogElement}
        />

        {/* Sección de GeoTrim en la URL (IT-805): permite refrescar y compartir
            el enlace de la tabla de CTs (/ecotrim/catalog/cts). Sin segmento se
            entra por el mapa. */}
        <Route
          path="/ecotrim/catalog/:section"
          element={geotrimCatalogElement}
        />

        {/* Datadis - Redirige a EcotrimArea */}
        <Route
          path="/datadis"
          element={<Navigate to="/ecotrim-area/datadis/" replace />}
        />

        {/* EcotrimArea - Nueva área principal de Ecotrim con Dashboard y Simulaciones */}
        <Route
          path="/ecotrim-area"
          element={
            <ProtectedRoute>
              <EcotrimArea onNavigate={navigate} section="dashboard" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/ecotrim-area/simulaciones"
          element={<Navigate to="/ecotrim-area" replace />}
        />

        <Route
          path="/ecotrim-area/proyectos"
          element={
            <ProtectedRoute>
              <EcotrimArea onNavigate={navigate} section="projects" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/ecotrim-area/proyecto/:projectId"
          element={
            <ProtectedRoute>
              <EcotrimArea onNavigate={navigate} section="projectDetail" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/ecotrim-area/proyecto/:projectId/optrim"
          element={
            <ProtectedRoute>
              <AdminRoute redirectTo="/ecotrim-area">
                <EcotrimArea onNavigate={navigate} section="optrim" />
              </AdminRoute>
            </ProtectedRoute>
          }
        />

        {/* Sección de Optrim en la URL: permite refrescar y compartir el enlace
            de una sección concreta (.../optrim/operation). Sin segmento se
            entra por Resumen. */}
        <Route
          path="/ecotrim-area/proyecto/:projectId/optrim/:section"
          element={
            <ProtectedRoute>
              <AdminRoute redirectTo="/ecotrim-area">
                <EcotrimArea onNavigate={navigate} section="optrim" />
              </AdminRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/ecotrim-area/proyecto/:projectId/colectivo/:collectiveId"
          element={
            <ProtectedRoute>
              <AdminRoute>
                <EcotrimArea onNavigate={navigate} section="collectiveConfig" />
              </AdminRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/ecotrim-area/recursos"
          element={
            <ProtectedRoute>
              <EcotrimArea onNavigate={navigate} section="resources" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/ecotrim-area/datadis"
          element={
            <ProtectedRoute>
              <EcotrimArea onNavigate={navigate} section="datadis" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/ecotrim-area/validation"
          element={
            <ProtectedRoute>
              <ValidationDashboard />
            </ProtectedRoute>
          }
        />

        <Route
          path="/ecotrim-area/validation/:trafoId"
          element={
            <ProtectedRoute>
              <ValidationDashboard />
            </ProtectedRoute>
          }
        />

        {/* Páginas informativas */}

        <Route path="/contacto" element={<Navigate to="/contact/" replace />} />
        <Route path="/privacidad" element={<Privacity />} />
        <Route path="/accesibilidad" element={<Accesibility />} />
        <Route path="/legal" element={<LegalAdvise />} />
        <Route path="/cookies" element={<Cookies />} />

        {/* Autenticación y Registro */}
        <Route path="/login" element={<UserLogin onNavigate={navigate} mode="login" />} />

        {/* Legacy: /register redirige a /pricing (flujo pricing → wizard → Stripe) */}
        <Route path="/register" element={<RegisterFlow />} />
        <Route path="/register/select-plan" element={<RegisterFlow />} />

        {/* Rutas protegidas - Área de usuario */}
        {/* Punto de entrada a mi-area ahora es herramientas */}
        <Route
          path="/mi-area"
          element={
            <ProtectedRoute>
              <UserArea onNavigate={navigate} section="tools" />
            </ProtectedRoute>
          }
        />

        {/* Rutas específicas para secciones del área de usuario */}
        <Route
          path="/mi-area/herramientas"
          element={
            <ProtectedRoute>
              <UserArea onNavigate={navigate} section="tools" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/mi-area/perfil"
          element={
            <ProtectedRoute>
              <UserArea onNavigate={navigate} section="profile" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/mi-area/compras"
          element={
            <ProtectedRoute>
              <UserArea onNavigate={navigate} section="purchases" />
            </ProtectedRoute>
          }
        />

        <Route
          path="/mi-area/suscripcion"
          element={
            <ProtectedRoute>
              <UserArea onNavigate={navigate} section="subscription" />
            </ProtectedRoute>
          }
        />

        {/* Integraciones (IT-976): MCP remoto, uso y clientes conectados */}
        <Route
          path="/mi-area/integraciones"
          element={
            <ProtectedRoute>
              <UserArea onNavigate={navigate} section="integrations" />
            </ProtectedRoute>
          }
        />

        {/* Consentimiento OAuth del MCP: gestiona el login él mismo para conservar la query */}
        <Route path="/mcp/autorizar" element={<McpAuthorize />} />

        {/* Mi equipo (IT-666): visible solo para super admins de equipo */}
        <Route
          path="/mi-area/equipo"
          element={
            <ProtectedRoute>
              <UserArea onNavigate={navigate} section="team" />
            </ProtectedRoute>
          }
        />

        {/* Panel de administración (IT-700): solo platform admins */}
        <Route
          path="/mi-area/trimergy-admin/*"
          element={
            <ProtectedRoute>
              <PlatformAdminRoute redirectTo="/mi-area">
                <PlatformAdminArea />
              </PlatformAdminRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/historial-simulaciones"
          element={
            <ProtectedRoute>
              <SimulationHistory />
            </ProtectedRoute>
          }
        />

        {/* Rutas de pago */}
        <Route
          path="/payment/success"
          element={
            <ProtectedRoute>
              <PaymentSuccess />
            </ProtectedRoute>
          }
        />

        <Route
          path="/subscription/success"
          element={
            <ProtectedRoute>
              <SubscriptionSuccess />
            </ProtectedRoute>
          }
        />

        {/* Rutas de suscripción */}
        <Route
          path="/subscription-success"
          element={
            <ProtectedRoute>
              <SubscriptionSuccess />
            </ProtectedRoute>
          }
        />

        {/* Debug y desarrollo */}
        <Route path="/debug-reports" element={<DebugReports />} />
        <Route path="/test-navigation" element={<NavigationTest />} />

        {/* Ruta 404 - Página no encontrada */}
        <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </NavigationWrapper>
  );
};

// Componente principal con providers
const AppContent = () => {
  // Login global desactivado - acceso directo a la aplicación
  return (
    <CalculatorProvider>
      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        <AppRoutes />
        <WhatsAppFloat />
      </div>
    </CalculatorProvider>
  );
};

// Componente raíz con todos los providers
const AppRouter = () => {
  return (
    <CookieConsentProvider>
      <PostHogWithConsent>
        <AuthProvider>
          <BrowserRouter unstable_useTransitions={false}>
            <EnsureTrailingSlash />
            <ScrollToTop />
            <DiscardUnpaidSignup />
        <AppContent />
        <CookieConsentBanner />
        <CookiePreferencesTrigger />
          </BrowserRouter>
        </AuthProvider>
      </PostHogWithConsent>
    </CookieConsentProvider>
  );
};

export default AppRouter;